import axios from "axios";
import { useTranslation } from "react-i18next";
import Layout from "../../components/layout/Layout";
import { Button, Flex, Form, FormProps, Input, Select, Upload, UploadFile, message, } from "antd";
import { UploadOutlined, SaveOutlined } from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";

import { useStore } from "../../stores/root-store-context";
import { UploadChangeParam } from "antd/es/upload";
import TextArea from "antd/es/input/TextArea";
import ReCAPTCHA from 'react-google-recaptcha';
import ApiStore from "../../stores/ApiStore";
import TicketSupportSystem from "./TicketSupportPage";
import { AuthorType, NewTicket } from "../../api/TicketApi";
import TicketChatStore from "../../stores/TicketChatStore";
import i18n from "../../i18n";
const { Option } = Select;

const SupportPage = observer(() => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const uploadRef = useRef<HTMLInputElement>(null);
    const [captchaValidated, setCaptchaValidated] = useState(false);
    const [formValid, setFormValid] = useState(false);
    const { applicationStore, employeesStore, ticketChatStore, kanbanTaskStore } = useStore();
    const [messageApi] = message.useMessage();
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const recaptchaRef = useRef<ReCAPTCHA | null>(null);

    const fileChange = (info: UploadChangeParam) => {
        let fileList = [...info.fileList];

        setFileList(fileList);

        if (info.file.status === "done") {
            message.success(`${info.file.name} ${t("UploadSuccess")}`);
        } else if (info.file.status === "error") {
            message.error(`${info.file.name} ${t("UploadFailed")}`);
        }
    };

    // Helper function to attach files to a task
    const attachFilesToTask = async (taskId: string) => {
        if (!taskId || fileList.length === 0) return;

        try {
            // Track uploading progress
            message.open({
                type: "loading",
                content: t("AttachingFiles"),
                key: "attaching-files",
            });

            // Keep track of successful uploads
            let successCount = 0;

            // Process each file
            for (const file of fileList) {
                if (file.originFileObj) {
                    try {
                        // Generate timestamp for the filename
                        const now = new Date();
                        const timestamp = now.getFullYear().toString() +
                            (now.getMonth() + 1).toString().padStart(2, '0') +
                            now.getDate().toString().padStart(2, '0') +
                            now.getHours().toString().padStart(2, '0') +
                            now.getMinutes().toString().padStart(2, '0') +
                            now.getSeconds().toString().padStart(2, '0');

                        // Create a new file with the timestamped name
                        const originalName = file.name;
                        const lastDotIndex = originalName.lastIndexOf('.');
                        const fileName = lastDotIndex > 0 ? originalName.substring(0, lastDotIndex) : originalName;
                        const fileExt = lastDotIndex > 0 ? originalName.substring(lastDotIndex) : '';
                        const newFileName = `${fileName}_${timestamp}${fileExt}`;

                        // Create a new File object with the modified name
                        const timestampedFile = new File(
                            [file.originFileObj],
                            newFileName,
                            { type: file.originFileObj.type }
                        );

                        // Use the kanbanTaskStore to upload the attachment with timestamped name
                        const result = await kanbanTaskStore.uploadTaskAttachment(
                            taskId.toString(),
                            timestampedFile
                        );

                        if (result) {
                            successCount++;
                            console.log(`Successfully attached file ${newFileName} to task ${taskId}`);
                        }
                    } catch (fileError) {
                        console.error(`Error attaching file ${file.name}:`, fileError);
                    }
                }
            }

            // Show success message for attachments
            message.destroy("attaching-files");
            if (successCount > 0) {
                message.success(`${successCount} ${successCount === 1 ? t("FileAttached") : t("FilesAttached")}`);
            }
        } catch (error) {
            message.destroy("attaching-files");
            console.error("Error attaching files to task:", error);
            message.error(t("FailedToAttachFiles"));
        }
    };

    const onFinish: FormProps<any>["onFinish"] = async (formData: any) => {
        message.open({
            type: "loading",
            content: t("SendingRequest"),
            key: "sending",
        });

        try {
            const data = new FormData();
            data.append("product", "SKTECH");
            data.append("name", formData.name);
            data.append("email", formData.email);
            data.append("problem", formData.problem);
            data.append("g_recaptcha_response", recaptchaRef.current?.getValue() || "");
            data.append("form-type", "support");
            data.append("counter", "0");


            const formDataMessage = new FormData();
            formDataMessage.append('text', formData.problem);
            formDataMessage.append('username', formData.name);
            formDataMessage.append('email', formData.email);
            formDataMessage.append('role', 'Unregistered User');
            formDataMessage.append('authorType', AuthorType.User.toString());

            if (fileList.length == 0) {
                data.append("files", "");
                formDataMessage.append('files', "");
            }

            // Add timestamp to each filename
            fileList.forEach((file) => {
                if (file.originFileObj) {
                    // Generate timestamp for the filename
                    const now = new Date();
                    const timestamp = now.getFullYear().toString() +
                        (now.getMonth() + 1).toString().padStart(2, '0') +
                        now.getDate().toString().padStart(2, '0') +
                        now.getHours().toString().padStart(2, '0') +
                        now.getMinutes().toString().padStart(2, '0') +
                        now.getSeconds().toString().padStart(2, '0');

                    // Create a new file with the timestamped name
                    const originalName = file.name;
                    const lastDotIndex = originalName.lastIndexOf('.');
                    const fileName = lastDotIndex > 0 ? originalName.substring(0, lastDotIndex) : originalName;
                    const fileExt = lastDotIndex > 0 ? originalName.substring(lastDotIndex) : '';
                    const newFileName = `${fileName}_${timestamp}${fileExt}`;

                    // Create a new File object with the modified name
                    const timestampedFile = new File(
                        [file.originFileObj],
                        newFileName,
                        { type: file.originFileObj.type }
                    );
                    data.append("files", timestampedFile);
                    formDataMessage.append('files', timestampedFile);
                }
            });

            const newTicket: NewTicket = {
                companyName: "sktech",
                companySystemName: formData.email,
                email: formData.email,
                category: formData.category,
                subject: "Unregistered User Message",
                description: formData.problem,
                userName: formData.name,
                userRole: "Unregistered User",
                lang: i18n.language
            }
            applicationStore.ApiStore.TicketApi.add(newTicket).then((response) => {
                console.log("Response:", response);

                applicationStore.ApiStore.TicketApi.addMessage(response.value.id, formDataMessage).then(() => {
                    message.success(t("TicketCreated"));
                    form.resetFields();
                    setFileList([]);
                    recaptchaRef.current?.reset();
                    setFormValid(false);
                    setCaptchaValidated(false);
                });
            })



            // applicationStore.ApiStore.CommonApiClient.SupportRequest(data).then(async (response) => {
            //     console.log("Response:", response);

            //     let createdTaskId = null;

            //     // Create a task when we get a report for project 85 and company user 585
            //     try {
            //         // Create task for project 85 and user 585
            //         const projectId = 86; // Actual data
            //         const companyUserId = 537;  // Actual data

            //             // Format the description for rich text editor (JSON format)
            //             const richTextDescription = JSON.stringify({
            //                 time: Date.now(),
            //                 blocks: [
            //                     {
            //                         id: `sr-${Math.random().toString(36).substr(2, 9)}`,
            //                         type: "paragraph",
            //                         data: {
            //                             text: `Support Request from: ${formData.name} (${formData.email})`
            //                         }
            //                     },
            //                     {
            //                         id: `pb-${Math.random().toString(36).substr(2, 9)}`,
            //                         type: "paragraph",
            //                         data: {
            //                             text: formData.problem
            //                         }
            //                     },
            //                     {
            //                         id: `dt-${Math.random().toString(36).substr(2, 9)}`,
            //                         type: "paragraph",
            //                         data: {
            //                             text: `Request received at: ${new Date().toLocaleString()}`
            //                         }
            //                     }
            //                 ],
            //                 version: "2.31.0-rc.7"
            //             });


            //             // Create the task
            //             const newTask = {
            //                 title: `SkTech Support Request: ${formData.name}`,
            //                 description: richTextDescription,
            //                 projectId: projectId,
            //                 statusId: -1,
            //                 priority: 2, // Medium priority
            //                 createdByUserId: companyUserId,
            //                 assigneeId: companyUserId // Assign to the same user
            //             };

            //             const createdTask = await kanbanTaskStore.createTask(newTask);
            //             if (createdTask && createdTask.id) {
            //                 createdTaskId = createdTask.id;
            //                 console.log("Task created successfully for support request, ID:", createdTaskId);

            //                 // Attach files to the newly created task
            //                 await attachFilesToTask(createdTaskId);
            //             }
            //     } catch (taskError) {
            //         console.error("Error creating task:", taskError);
            //         // Don't show error to user as the support request was still successful
            //     }

            //     form.resetFields();
            //     setFileList([]);
            // }).finally(() => {
            //     message.destroy("sending");
            //     message.success(t("RequestSucessfulSendWaitResponsEmail"));
            // });
        } catch (error: any) {
            message.destroy("sending");
            message.error(t("Unexpected error!") + error.message);
            console.error("Unexpected error!:", error);
        }
    };

    const handleCaptchaChange = (value: any) => {
        // setCaptchaValidated(!!value);
    };

    const handleFormChange = async () => {
        const isValid = await form.validateFields().then(
            () => true,
            () => false
        );
        console.log(isValid);

        setFormValid(isValid);
    };

    return (
        <Layout headerStyle={1} footerStyle={2} breadcrumbTitle={t("Support")}>
            <div className="container" style={{ maxWidth: 800, marginTop: 20 }}>
                <h2 className="center" style={{ textTransform: "none" }}>{t("DescribeProblem")}</h2>
                <Form form={form} onFinish={onFinish} layout="vertical" onValuesChange={handleFormChange}>
                    <Form.Item<any> name="product" hidden>
                        <Input value={"SKTECH"} />
                    </Form.Item>
                    <Form.Item<any>
                        label={`${t("user.Name")}`}
                        name="name"
                        style={{ width: "100%" }}
                        rules={[{ required: true }]}
                    >
                        <Input placeholder={`${t("user.Name")}`} />
                    </Form.Item>

                    <Form.Item<any>
                        name="email"
                        label={`${t("emailLabel")}`}
                        style={{ width: "100%" }}
                        rules={[{
                            required: true,
                            message: t('email is required!'),
                        }]} >
                        <Input placeholder={t('emailLabel')} pattern="^[^@\s]+@[^@\s]+\.[^@\s]+$" />
                    </Form.Item>
                    <Form.Item
                        name="category"
                        label="Категорія"
                        rules={[{ required: true, message: 'Please select a category' }]}
                        initialValue="technical"
                    >
                        <Select>
                            <Option value="payment">{t('supportCategories.payment')}</Option>
                            <Option value="project">{t('supportCategories.project')}</Option>
                            <Option value="employee">{t('supportCategories.employee')}</Option>
                            <Option value="tests">{t('supportCategories.tests')}</Option>
                            <Option value="time">{t('supportCategories.time')}</Option>
                            <Option value="calendar">{t('supportCategories.calendar')}</Option>
                            <Option value="technical">{t('supportCategories.technical')}</Option>
                            <Option value="advice">{t('supportCategories.advice')}</Option>
                            <Option value="other">{t('supportCategories.other')}</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item<any>
                        label={`${t("Problem")}`}
                        name="problem"
                        style={{ width: "100%" }}
                        rules={[{ required: true }]}
                    >
                        <TextArea placeholder={`${t("DescribeProblem")}`} rows={5} />
                    </Form.Item>
                    <Form.Item<any> name="files" style={{ width: "100%" }}>
                        <Upload
                            ref={uploadRef}
                            style={{ alignSelf: "center" }}
                            maxCount={3}
                            multiple
                            listType="text"
                            fileList={fileList}
                            onChange={(info) => {
                                if (info.fileList.length > 3) {
                                    message.warning(t("MaxFilesLimit", { count: 3 }) || "Максимум 3 файли");
                                    return;
                                }

                                setFileList(info.fileList);

                                if (info.file.status === "done") {
                                    message.success(`${info.file.name} ${t("UploadSuccess")}`);
                                } else if (info.file.status === "error") {
                                    message.error(`${info.file.name} ${t("UploadFailed")}`);
                                }
                            }}
                            beforeUpload={(file) => {
                                const isLt30M = file.size / 1024 / 1024 <= 10;
                                if (!isLt30M) {
                                    message.error(`${file.name}: ${t("FileSizeLimit", { size: 10 }) || "Файл перевищує 10MB"}`);
                                    return Upload.LIST_IGNORE;
                                }
                                return false; 
                            }}
                        >
                            <Button icon={<UploadOutlined />}>{t('AddFiles')}</Button>
                        </Upload>
                        <div style={{ marginTop: 8, color: "#999" }}>
                            {t("UploadLimitHint") || "Максимум 3 файли, розмір кожного до 30MB"}
                        </div>
                    </Form.Item>
                    <Form.Item<any>
                        name="recaptcha"
                        style={{ width: "100%" }}
                        rules={[{ required: true }]}
                    >
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey="6Lc6nZkqAAAAAOtTKcPhSoVwSod5rkHwc27x7YOK"
                            onChange={handleCaptchaChange}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button
                            type="primary"
                            className="theme-btn"
                            htmlType="submit"
                            style={{ height: 60, display: "flex", float: "left" }}
                        >
                            {`${t("SendRequest")}`}{" "}
                            <SaveOutlined style={{ fontSize: 20, margin: 5 }} />
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        </Layout>
    );
});

export default SupportPage;